import React, { useState, useEffect } from "react";
import { ColumnType } from "antd/es/table";
import { Icon } from "../../components/Component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import { updateReportInDetailModalSlice } from "../../store/slices/expenses/ReportInDetailView";
import TopMenu from "../../route/topMenu";
import ReportInDetailModal from "./ReportInDetailModal";
import { expensesApis } from "../../api/Expenses";
import Swal from "sweetalert2";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Card } from "reactstrap";
import { Button as AntButton, Modal as AntModal, Form, Input, Table, Empty, Tag, Dropdown, Menu } from "antd";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import SkeletonTable from "../../utils/skeleton-templates/table";
import Content from "../../layout/content/Content";

interface PendingApproval {
  expenseReportId: number;
  orgId: number;
  reportId: number;
  createdBy: number;
  status: string;
  // Additional fields for display
  reportName?: string;
  userName?: string;
  createdByName?: string;
  submitDate?: string;
  dateRange?: string;
  total?: number;
  toBeReimbursed?: number;
  // Report-level information
  totalIn?: number;
  totalOut?: number;
  balance?: number;
  expenseCount?: number;
  pendingApprovalCount?: number;
  // Pending-specific fields from fetchReport API
  userPendingTotalIn?: number;
  userPendingTotalOut?: number;
  userPendingBalance?: number;
  userPendingReimbursableAmount?: number;
  userPendingReimbursableBalance?: number;
  userPendingNonReimbursableAmount?: number;
  userPendingTaxAmount?: number;
  userPendingReimbursableTaxAmount?: number;
  userUnclaimedAdvanceAmount?: number;
}

interface ExpenseReport {
  id: string;
  reportName: string;
  status: string;
  submittedDate: string;
  approvedDate?: string;
  totalAmount: number;
  expenses: number;
  total: string;
  amountToBeReimbursed: string;
  submitter?: string;
  dateRange?: string;
  paginationId?: string;
}

const PendingApproval = () => {
  const methods = useForm({
    mode: "onChange",
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sm, setSm] = useState(false);
  const { projectName, projectId } = location.state || {};
  const [projectIdState, setProjectIdState] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [paginationId, setPaginationId] = useState<number | null>(null);

  // Modal state variables
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isBulkReject, setIsBulkReject] = useState(false);



// Helper function for error handling
const showErrorAlert = (error: any, defaultMessage: string) => {
  const errorMessage = error?.response?.data?.message || error?.message || defaultMessage;
  Swal.fire({
    title: "Error!",
    text: errorMessage,
    icon: "error",
    confirmButtonText: "OK"
  });
};

// Helper function for success handling with refresh
const showSuccessAndRefresh = async (message: string, count?: number) => {
  const successMessage = count ? `${count} expense report(s) ${message}` : message;
  Swal.fire({
    icon: "success",
    title: "Success",
    text: successMessage,
    confirmButtonText: "Ok",
  }).then(async () => {
    setTimeout(async () => {
      try {
        await loadPendingApprovals({}, true);
      } catch (refreshError) {
        // Failed to refresh pending approvals
      }
    }, 500);
  });
};

const loadPendingApprovals = async (params?: any, forceRefresh = false) => {
  setLoading(true);
  
  if (forceRefresh) {
    setPendingApprovals([]);
    setPaginationId(null);
  }
  
  try {
    if (!projectIdState) {
      throw new Error("Project ID is required");
    }

    const apiParams = {
      sortOrder: "DESC",
      direction: "P",
      pageSize: params?.pageSize || pagination.pageSize,
      pageDiff: 0,
      projectIds: projectIdState.toString(),
      paginationId: params?.paginationId || undefined,
      paymentStatus: "Pending",
      ...params,
    };

    const response = await expensesApis.fetchExpensesApprovals(apiParams);

    if (response && response.status) {
      const approvalsList = response.data?.list || response.data || [];
      const totalCount = response.data?.count || response.data?.total || approvalsList.length;
      const nextPaginationId = response.data?.paginationId || null;

      const transformedApprovals: PendingApproval[] = [];
      
      approvalsList.forEach((report: any) => {
        const pendingApprovalCount = report.expenseApprovals ? 
          report.expenseApprovals.filter((approval: any) => 
            approval.status?.toLowerCase() === 'pending' || 
            approval.status?.toLowerCase() === 'pending_approval'
          ).length : 0;
        
        if (pendingApprovalCount > 0 || report.reportStatus?.toLowerCase() === 'pending') {
          transformedApprovals.push({
            expenseReportId: report.expenseReportId,
            orgId: report.orgId,
            reportId: report.expenseReportId,
            createdBy: report.createdBy,
            status: report.reportStatus || 'PENDING',
            reportName: report.reportName,
            userName: report.createdByName || `User ${report.createdBy}`,
            createdByName: report.createdByName || `User ${report.createdBy}`,
            submitDate: report.createdAt ? dayjs(report.createdAt).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
            dateRange: report.startDate && report.endDate 
              ? `${dayjs(report.startDate).format('DD-MMM-YYYY')} - ${dayjs(report.endDate).format('DD-MMM-YYYY')}`
              : dayjs().format('DD-MMM-YYYY'),
            total: report.pendingTotalOut || 0,
            toBeReimbursed: report.reimbursableAmount || 0,
            totalIn: report.totalIn || 0,
            totalOut: report.totalOut || 0,
            balance: report.balance || 0,
            expenseCount: report.expenses?.length || 0,
            pendingApprovalCount: pendingApprovalCount,
            userPendingTotalIn: report.userPendingTotalIn || 0,
            userPendingTotalOut: report.userPendingTotalOut || 0,
            userPendingBalance: report.userPendingBalance || 0,
            userPendingReimbursableAmount: report.userPendingReimbursableAmount || 0,
            userPendingReimbursableBalance: report.userPendingReimbursableBalance || 0,
            userPendingNonReimbursableAmount: report.userPendingNonReimbursableAmount || 0,
            userPendingTaxAmount: report.userPendingTaxAmount || 0,
            userPendingReimbursableTaxAmount: report.userPendingReimbursableTaxAmount || 0,
            userUnclaimedAdvanceAmount: report.userUnclaimedAdvanceAmount || 0,
          });
        }
      });

      setPendingApprovals(transformedApprovals);
      setPaginationId(nextPaginationId);
      setPagination(prev => ({
        ...prev,
        current: params?.current || prev.current,
        pageSize: params?.pageSize || prev.pageSize,
        total: totalCount,
      }));
    }
  } catch (error: any) {
    showErrorAlert(error, "Failed to load pending approvals");
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  dispatch(
    updateMenuData(
      TopMenu?.projectNav?.map((item: any) => {
        return {
          ...item,
          linkState: location.state,
        };
      })
    )
  );
}, [location, dispatch]);

useEffect(() => {
  const { id: projectIdFromState, projectId: projectIdFromStateAlt } = location.state || {};
  // Use URL param first, then fall back to location state
  const finalProjectId = projectIdFromUrl || projectIdFromState || projectIdFromStateAlt;
  if (finalProjectId && !isNaN(Number(finalProjectId))) {
    setProjectIdState(finalProjectId);
  }
}, [location.state, projectIdFromUrl]);

// Load data when projectId changes
useEffect(() => {
  if (projectIdState) {
    loadPendingApprovals();
  }
}, [projectIdState]);

// Handle opening ReportBreakDown when returning from NewExpense/NewAdvance view
useEffect(() => {
  const { openReportBreakDown, refreshReportBreakDown, reportId, reportName } = location.state || {};
  const resolvedProjectId = projectIdState;

  if (openReportBreakDown && reportId && resolvedProjectId) {
    // Try to find the report in current list first
    const report = pendingApprovals.find((r) => (r.expenseReportId || r.reportId)?.toString() === reportId.toString());

    if (report) {
      // Reuse existing handler to build full modal payload (includes pending flags)
      handleOpenReport({ ...report });
    } else {
      // Fallback minimal modal data; ReportBreakDown will fetch full data
      const modalData = {
        id: reportId.toString(),
        open: true,
        reportName: reportName || `Report ${reportId}`,
        activeTab: "expenses",
        projectResponse: {
          id: Number(resolvedProjectId),
          name: projectName || "Project",
          projectId: Number(resolvedProjectId),
        },
        extra: {
          submittedDate: dayjs().format('DD-MMM-YYYY'),
          totalAmount: 0,
          expenses: 0,
          total: "₹0",
          amountToBeReimbursed: "₹0",
          submitter: null,
          dateRange: dayjs().format('DD-MMM-YYYY'),
          createdBy: null,
          createdByName: null,
          // Pending specific defaults
          userPendingTotalIn: 0,
          userPendingTotalOut: 0,
          userPendingBalance: 0,
          userPendingReimbursableAmount: 0,
          userPendingNonReimbursableAmount: 0,
          userPendingTaxAmount: 0,
          userPendingReimbursableTaxAmount: 0,
          userUnclaimedAdvanceAmount: 0,
        },
        allowEdit: true,
        sourcePage: 'pendingApproval',
      } as any;
      dispatch(updateReportInDetailModalSlice(modalData));
    }

    // Clean flags from history state; never use null in URL
    const basePath = '/pms/expenses/pending-approval';
    const path =
      projectIdState != null && !isNaN(Number(projectIdState))
        ? `${basePath}/${projectIdState}`
        : basePath;
    navigate(path, {
      replace: true,
      state: {
        ...location.state,
        openReportBreakDown: false,
        refreshReportBreakDown: false,
      },
    });
  }
}, [location.state, projectIdState, pendingApprovals]);

// RejectModal component
const RejectModal = ({
  isModalVisible,
  setIsModalVisible,
  onReject,
  title = "Reject Item",
  label = "Reason for rejection",
  isLoading = false,
}: {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  onReject: (reason: string) => void;
  title?: string;
  label?: string;
  isLoading?: boolean;
}) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        onReject(reason);
        form.resetFields();
        setIsModalVisible(false);
      })
      .catch((info) => {
      });
  };

  return (
    <AntModal
      title={title}
      visible={isModalVisible}
      footer={null}
      onCancel={handleCancel}
    >
      <Form form={form} layout="vertical" name="rejectForm">
        <Form.Item
          name="reason"
          label={label}
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." />
        </Form.Item>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
                onClick={handleOk}
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={handleCancel}
                className="btn-dim btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      </Form>
    </AntModal>
  );
};

const tableOnChange = (paginationConfig: any, filters: any, sorter: any) => {
  const params = {
    current: paginationConfig.current,
    pageSize: paginationConfig.pageSize,
    paginationId: paginationId,
    ...filters,
    ...sorter,
  };
  loadPendingApprovals(params);
};

const filteredApprovals = pendingApprovals;

const handleOpenReport = async (report: any) => {
  const reportId = report.expenseReportId || report.reportId;
  if (!reportId) {
    Swal.fire({
      title: "Error",
      text: "Unable to open report: No valid report ID found",
      icon: "error",
      confirmButtonText: "OK"
    });
    return;
  }

  // Helper function to create modal data
  const createModalData = (reportData: any, expensesData: any, isDataFetched: boolean) => {
    const userId = report.createdBy || report.userId;
    const userName = `User ${userId}`;
    
    const transformedReport: ExpenseReport = {
      id: reportId.toString(),
      reportName: reportData.reportName || `Report ${reportId}`,
      status: reportData.reportStatus || report.status || "Pending",
      submittedDate: reportData.createdAt ? dayjs(reportData.createdAt).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
      totalAmount: reportData.totalOut || 0,
      expenses: reportData.expenses?.length || 1,
      total: `₹${(reportData.totalOut || 0).toLocaleString()}`,
      amountToBeReimbursed: `₹${(reportData.totalOut || 0).toLocaleString()}`,
      submitter: userName,
      dateRange: reportData.startDate && reportData.endDate
        ? `${dayjs(reportData.startDate).format('DD-MMM-YYYY')} - ${dayjs(reportData.endDate).format('DD-MMM-YYYY')}`
        : dayjs().format('DD-MMM-YYYY')
    };

    return {
      id: transformedReport.id,
      open: true,
      reportName: transformedReport.reportName,
      activeTab: "expenses",
      projectResponse: {
        id: Number(projectIdState),
        name: projectName || "Project",
        projectId: Number(projectIdState)
      },
      extra: {
        status: "Pending", // Always pass "Pending" status when coming from pending approvals
        submittedDate: transformedReport.submittedDate,
        approvedDate: transformedReport.approvedDate,
        totalAmount: transformedReport.totalAmount,
        expenses: transformedReport.expenses,
        total: transformedReport.total,
        amountToBeReimbursed: transformedReport.amountToBeReimbursed,
        submitter: transformedReport.submitter,
        dateRange: transformedReport.dateRange,
        paginationId: transformedReport.paginationId,
        totalIn: reportData.totalIn || 0,
        totalOut: reportData.totalOut || 0,
        balance: reportData.balance || 0,
        userPendingTotalIn: reportData.userPendingTotalIn || 0,
        userPendingTotalOut: reportData.userPendingTotalOut || 0,
        userPendingBalance: reportData.userPendingBalance || 0,
        userPendingReimbursableAmount: reportData.userPendingReimbursableAmount || 0,
        userPendingNonReimbursableAmount: reportData.userPendingNonReimbursableAmount || 0,
        userPendingTaxAmount: reportData.userPendingTaxAmount || 0,
        userPendingReimbursableTaxAmount: reportData.userPendingReimbursableTaxAmount || 0,
        userUnclaimedAdvanceAmount: reportData.userUnclaimedAdvanceAmount || 0,
        fetchedReportData: isDataFetched ? reportData : null,
        fetchedExpensesData: isDataFetched ? expensesData : null,
        createdBy: userId,
        createdByName: reportData.createdByName || reportData.createdBy || userId,
        dataAlreadyFetched: isDataFetched
      },
      allowEdit: true, // This enables approve/reject buttons in the modal
      sourcePage: "pendingApproval" // This tells ReportBreakDown to show approve/reject buttons and hide New Expense button
    };
  };

  try {
    setLoading(true);

    if (!projectIdState) {
      throw new Error("Project ID is required");
    }
    
    const reportResponse = await expensesApis.fetchReport(reportId, { 
      paymentStatus: "Pending"
    });
    
    const response = await expensesApis.fetchExpenses(projectIdState, { 
      reportId: reportId,
      paymentStatus: "Pending",
      pageSize: 100,
      pageDiff: 0,
      sortOrder: "DESC",
      direction: "P",
      projectId: projectIdState,
      expenseReportId: reportId
    });

    if (response && response.status && response.data) {
      const reportData = reportResponse && reportResponse.status && reportResponse.data 
        ? reportResponse.data 
        : response.data;
      
      const modalData = createModalData(reportData, response.data, true);
      dispatch(updateReportInDetailModalSlice(modalData));
    } else {
      // Fallback with minimal data
      const fallbackData = {
        reportName: `Report ${reportId}`,
        reportStatus: report.status || "Pending",
        createdAt: null,
        totalOut: 0,
        expenses: [],
        startDate: null,
        endDate: null,
        totalIn: 0,
        balance: 0,
        userPendingTotalIn: 0,
        userPendingTotalOut: 0,
        userPendingBalance: 0,
        userPendingReimbursableAmount: 0,
        userPendingNonReimbursableAmount: 0,
        userPendingTaxAmount: 0,
        userPendingReimbursableTaxAmount: 0,
        userUnclaimedAdvanceAmount: 0,
        createdByName: report.createdByName || report.createdBy,
        createdBy: report.createdBy
      };
      
      const modalData = createModalData(fallbackData, null, false);
      dispatch(updateReportInDetailModalSlice(modalData));
    }
  } catch (error) {
    // Error fallback with minimal data
    const errorData = {
      reportName: `Report ${reportId}`,
      reportStatus: report.status || "Pending",
      createdAt: null,
      totalOut: 0,
      expenses: [],
      startDate: null,
      endDate: null,
      totalIn: 0,
      balance: 0,
      userPendingTotalIn: 0,
      userPendingTotalOut: 0,
      userPendingBalance: 0,
      userPendingReimbursableAmount: 0,
      userPendingNonReimbursableAmount: 0,
      userPendingTaxAmount: 0,
      userPendingReimbursableTaxAmount: 0,
      userUnclaimedAdvanceAmount: 0,
      createdByName: report.createdByName || report.createdBy,
      createdBy: report.createdBy
    };
    
    const modalData = createModalData(errorData, null, false);
    dispatch(updateReportInDetailModalSlice(modalData));

    Swal.fire({
      title: "Info",
      text: "Showing basic report information. Some details may not be available.",
      icon: "info",
      confirmButtonText: "OK"
    });
  } finally {
    setLoading(false);
  }
};

const handleBulkRejectClick = () => {
  if (selectedRowKeys.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "No Selection",
      text: "Please select at least one report to reject",
      confirmButtonText: "Ok",
      customClass: {
        container: "border-class",
      },
    });
    return;
  }
  setIsBulkReject(true);
  setRejectModalVisible(true);
};

const handleBulkApprove = async () => {
  if (selectedRowKeys.length === 0) return;

  Swal.fire({
    title: "Are you sure you want to approve?",
    text: `You are about to approve ${selectedRowKeys.length} expense report(s). You won't be able to revert this!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, approve them!",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const reportIds = selectedRowKeys.join(',');
        const response = await updateBulkReportStatus(reportIds, 'APPROVED');
        const { data, status, message } = response || {};
        
        if (status) {
          // Clear selected checkboxes immediately
          setSelectedRowKeys([]);
          await showSuccessAndRefresh(data?.message || "approved successfully", selectedRowKeys.length);
        } else {
          showErrorAlert(null, message || "Failed to approve expense reports. Please try again.");
        }
      } catch (error: any) {
        showErrorAlert(error, "Failed to approve expense reports");
      }
    }
  });
};

const handleBulkReject = async (reason: string) => {
  if (selectedRowKeys.length === 0) return;

  Swal.fire({
    title: "Are you sure you want to reject?",
    text: `You are about to reject ${selectedRowKeys.length} expense report(s). You won't be able to revert this!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, reject them!",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        setIsRejecting(true);
        const reportIds = selectedRowKeys.join(',');
        const response = await updateBulkReportStatus(reportIds, 'REJECTED', reason);
        const { data, status, message } = response || {};
        
        if (status) {
          // Clear selected checkboxes immediately
          setSelectedRowKeys([]);
          await showSuccessAndRefresh(data?.message || "rejected successfully", selectedRowKeys.length);
        } else {
          showErrorAlert(null, message || "Failed to reject expense reports. Please try again.");
        }
      } catch (error: any) {
        showErrorAlert(error, "Failed to reject expense reports");
      } finally {
        setIsRejecting(false);
        setRejectModalVisible(false);
        setIsBulkReject(false);
      }
    }
  });
};

// Handle refresh when modal closes
useEffect(() => {
  const handleReportBreakDownClose = (event: any) => {
    // Clear selected checkboxes immediately
    setSelectedRowKeys([]);
    
    // Refresh pending approvals immediately without delay
    loadPendingApprovals({}, true);
  };

  window.addEventListener('reportBreakDownClosed', handleReportBreakDownClose);
  
  return () => {
    window.removeEventListener('reportBreakDownClosed', handleReportBreakDownClose);
  };
}, [projectIdState]);

const handleApprove = async (expenseReportId: number) => {
  Swal.fire({
    title: "Are you sure you want to approve?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, approve it!",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const response = await updateReportStatus(expenseReportId, 'APPROVED');
        const { data, status, message } = response || {};
        if (status) {
          await showSuccessAndRefresh(data?.message || "Expense report approved successfully");
        } else {
          showErrorAlert(null, message || "Failed to approve expense report. Please try again.");
        }
      } catch (error: any) {
        showErrorAlert(error, "Failed to approve expense report");
      }
    }
  });
};

const handleReject = async (expenseReportId: number, reason: string) => {
  Swal.fire({
    title: "Are you sure you want to reject?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, reject it!",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        setIsRejecting(true);
        const response = await updateReportStatus(expenseReportId, 'REJECTED', reason);
        const { data, status, message } = response || {};
        if (status) {
          await showSuccessAndRefresh(data?.message || "Expense report rejected successfully");
        } else {
          showErrorAlert(null, message || "Failed to reject expense report. Please try again.");
        }
      } catch (error: any) {
        showErrorAlert(error, "Failed to reject expense report");
      } finally {
        setIsRejecting(false);
        setRejectModalVisible(false);
        setIsBulkReject(false);
      }
    }
  });
};

const handleRejectConfirm = async (reason: string) => {
  try {
    if (isBulkReject) {
      await handleBulkReject(reason);
    }
  } catch (error) {
    // Error handling is done in the individual functions
  }
};

// Helper function to validate API response
const validateApiResponse = (response: any, action: string) => {
  if (response && response.response && response.response.data) {
    const errorData = response.response.data;
    throw new Error(errorData.message || `Failed to ${action} report`);
  }

  const isSuccess = response?.status === true || 
                   response?.data?.status === true || 
                   response?.status === 200 ||
                   (response?.data && !response?.error);

  if (!isSuccess) {
    const errorMessage = response?.data?.message || 
                        response?.message || 
                        response?.error?.message ||
                        `Failed to ${action} report`;
    throw new Error(errorMessage);
  }

  return response;
};

const updateReportStatus = async (expenseReportId: number, status: string, reason?: string) => {
  try {
    let response;

    if (status === 'APPROVED') {
      const approvePayload = { reportId: expenseReportId };
      response = await expensesApis.approveReport(approvePayload);
    } else if (status === 'REJECTED') {
      if (!reason) {
        throw new Error('Rejection reason is required');
      }
      const rejectPayload = { reportId: expenseReportId, reason: reason };
      response = await expensesApis.rejectReport(rejectPayload);
    }

    return validateApiResponse(response, status.toLowerCase());
  } catch (error: any) {
    throw error;
  }
};

const updateBulkReportStatus = async (reportIds: string, status: string, reason?: string) => {
  try {
    let response;

    if (status === 'APPROVED') {
      const approvePayload = { reportId: reportIds };
      response = await expensesApis.approveReport(approvePayload as any);
    } else if (status === 'REJECTED') {
      if (!reason) {
        throw new Error('Rejection reason is required');
      }
      const rejectPayload = { reportId: reportIds, reason: reason };
      response = await expensesApis.rejectReport(rejectPayload as any);
    }

    return validateApiResponse(response, status.toLowerCase() + 's');
  } catch (error: any) {
    throw error;
  }
};


const handleIdClick = async (reportId: string) => {
  if (!reportId) return;

  try {
    const report = {
      expenseReportId: parseInt(reportId),
      reportName: `Report ${reportId}`,
      createdBy: 0,
      reportStatus: 'PENDING'
    };
    
    await handleOpenReport(report);
  } catch (error: any) {
    showErrorAlert(error, "Failed to open report");
  }
};

const pendingApprovalColumns: ColumnType<any>[] = [
  {
    title: "Expense Header",
    dataIndex: "reportName",
    key: "reportName",
    width: 250,
    render: (text: string, record: any) => {
      const reportName = text || record.reportName || `Report ${record.reportId || 'Unknown'}`;
      return (
        <span
          className="inlineTableCellLink"
          style={{
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "middle",
            cursor: "pointer",
            color: "#2557d6",
            fontWeight: "600",
            fontSize: "14px",
            fontFamily: "Inter, Roboto, sans-serif"
          }}
          onClick={() => handleOpenReport(record)}
          title={reportName}
        >
          {reportName}
        </span>
      );
    },
  },
  {
    title: "Submitted By",
    dataIndex: "createdByName",
    key: "createdByName",
    width: 120,
    render: (createdByName: string, record: any) => {
      const userName = createdByName || record.userName || record.createdByName || `User ${record.createdBy || 'Unknown'}`;
      return (
        <span style={{ fontSize: "14px", fontFamily: "Inter, Roboto, sans-serif" }}>
          {userName}
        </span>
      );
    },
  },
  {
    title: "Created Date",
    dataIndex: "submitDate",
    key: "submitDate",
    width: 100,
    render: (date: string) => date || 'N/A',
  },
  {
    title: "Date Range",
    dataIndex: "dateRange",
    key: "dateRange",
    width: 180,
    render: (dateRange: string) => dateRange || 'N/A',
  },
  {
    title: "Total Amount",
    dataIndex: "total",
    key: "total",
    width: 90,
    render: (total: number) => {
      return (
        <span style={{ fontSize: "14px", fontFamily: "Inter, Roboto, sans-serif", fontWeight: "500" }}>
          ₹{(total || 0).toLocaleString()}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (status: string) => (
      <Tag 
        color={
          status.toLowerCase().includes("draft") ? "geekblue" :
          status.toLowerCase().includes("pending") ? "gold" :
          status.toLowerCase().includes("approved") ? "green" : 
          status.toLowerCase().includes("rejected") ? "red" : "default"
        } 
        style={{ fontSize: 14, padding: '2px 12px' }}
      >
        {(status || 'PENDING').charAt(0).toUpperCase() + (status || 'PENDING').slice(1).toLowerCase()}
      </Tag>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 100,
    render: (_: any, record: any) => {
      const menuItems = [
        {
          key: 'view',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
              <Icon name="eye" style={{ fontSize: '12px' }} />
              <span style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>View</span>
            </div>
          ),
          onClick: () => handleIdClick(record.expenseReportId?.toString() || '')
        }
      ];

      const menu = <Menu items={menuItems} style={{ minWidth: '180px' }} />;

      return (
        <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
          <a
            style={{
              border: 'none',
              background: 'transparent',
              padding: '4px 8px',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              textDecoration: 'none'
            }}
          >
            <Icon name="more-h" style={{ fontSize: '16px', color: '#666' }} />
          </a>
        </Dropdown>
      );
    },
  },
];


// Row selection configuration
const rowSelection = {
  selectedRowKeys,
  onChange: (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  },
  onSelect: (record: any, selected: boolean, selectedRows: any[], nativeEvent: Event) => {
  },
  onSelectAll: (selected: boolean, selectedRows: any[], changeRows: any[]) => {
  },
  getCheckboxProps: (record: any) => ({
    name: record.expenseReportId || record.id,
  }),
};

// Top right JSX for bulk actions
const topRightJSX = selectedRowKeys.length > 0 ? (
  <>
    <li>
      <AntButton
        style={{
          backgroundColor: "#d1f5ec",
          color: "#107154",
          borderColor: "#d1f5ec",
          marginRight: "8px",
        }}
        onClick={handleBulkApprove}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#38d39f";
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.borderColor = "#38d39f";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#d1f5ec";
          e.currentTarget.style.color = "#107154";
          e.currentTarget.style.borderColor = "#d1f5ec";
        }}
      >
        Approve
      </AntButton>
    </li>
    <li>
      <AntButton
        style={{
          backgroundColor: "#fbe3e4",
          color: "#a94442",
          borderColor: "#fbe3e4",
          marginRight: "8px",
        }}
        onClick={handleBulkRejectClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#e74c3c";
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.borderColor = "#e74c3c";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#fbe3e4";
          e.currentTarget.style.color = "#a94442";
          e.currentTarget.style.borderColor = "#fbe3e4";
        }}
      >
        Reject
      </AntButton>
    </li>
  </>
) : null;


return (
  <>
    <Content>
      <Card className="card-bordered" style={{ margin: "0", overflow: "hidden" }}>
          <div
            className="card-inner card-inner-lg"
            style={{
              padding: "0",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <FormProvider {...methods}>
              <form style={{ height: "100%", display: "flex", flexDirection: "column" }}>

                {/* Header Section */}
                <div style={{ borderBottom: "1px solid #e5e5e5", flexShrink: 0 }}>
                  <div
                    className="nk-ibx-head w-100 d-flex justify-content-between align-items-center"
                    style={{ padding: "16px 24px 0 24px" }}>
                    <h5 style={{ margin: "0", fontWeight: "600" }}>Pending Approval - Expenses</h5>
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                        {/* Bulk action buttons */}
                        {topRightJSX}
                        <li>
                          <UncontrolledDropdown>
                            <DropdownToggle
                              tag="a"
                              className="dropdown-toggle btn btn-icon btn-trigger"
                              onClick={(ev) => {
                                loadPendingApprovals();
                              }}
                            >
                              <Icon name="redo"></Icon>
                            </DropdownToggle>
                          </UncontrolledDropdown>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Table Content Section */}
                <div
                  style={{
                    flex: "1",
                    padding: "10px",
                    overflow: "auto",
                    backgroundColor: "#fff",
                    position: "relative",
                    minHeight: 0
                  }}
                >
                  <SkeletonTable
                    loading={loading}
                    columns={pendingApprovalColumns as any[]}
                  >
                    <Table
                      rowKey="expenseReportId"
                      columns={pendingApprovalColumns}
                      dataSource={filteredApprovals}
                      pagination={pagination}
                      onChange={tableOnChange}
                      rowSelection={rowSelection}
                      bordered
                      size="middle"
                      scroll={{
                        x: 1200,
                        y: "calc(100vh - 200px)"
                      }}
                      style={{
                        position: 'relative',
                        zIndex: 1
                      }}
                      components={{
                        body: {
                          wrapper: (props: any) => (
                            <tbody {...props} style={{ position: 'relative', zIndex: 1 }} />
                          )
                        }
                      }}
                      locale={{
                        emptyText: (
                          <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "200px",
                            backgroundColor: "white",
                          }}>
                            <Empty description="No Pending Approvals Found" />
                          </div>
                        ),
                      }}
                    />
                  </SkeletonTable>
                </div>
              </form>
            </FormProvider>
          </div>
      </Card>
    </Content>

    <RejectModal
      isModalVisible={rejectModalVisible}
      setIsModalVisible={setRejectModalVisible}
      onReject={handleRejectConfirm}
      title={
        isBulkReject
          ? `Reject ${selectedRowKeys.length} Selected Expense Report(s)`
          : `Reject Expense Report`
      }
      label="Reason for rejection"
      isLoading={isRejecting}
    />
    <ReportInDetailModal />
  </>
);
};

export default PendingApproval;